import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Option } from "@/types/domain";
import { matchesTerms, searchTerms } from "./searchMatch";
import { useAnchoredMenu } from "./useAnchoredMenu";
import { ColorText } from "./InlineDropdown";

/**
 * 폼(비-표) 영역에서 쓰는 검색형 드롭다운.
 * 요구사항 §9-3 — 범주형 입력은 전부 dropdown + 검색.
 * 아이콘을 쓰지 않으므로 열림 상태는 테두리와 목록 표시로만 나타낸다.
 *
 * **입력칸 자체가 검색창이다** (2026-08-20). 예전에는 트리거가 버튼이고 검색창이 목록
 * 안에 따로 있어서 "누르고 → 목록 안 검색칸으로 커서를 옮기고 → 친다" 세 동작이 필요했다.
 * 지금은 칸을 누르면 곧바로 커서가 서고 타이핑이 그대로 필터가 된다. 표 안에서 쓰는
 * `InlineDropdown` 도 같은 방식이라 두 컴포넌트의 조작감이 같다.
 *
 * 열려 있는 동안 입력칸은 **검색어**를 담고, 지금 고른 값은 placeholder 로 남는다 —
 * 검색어를 지웠을 때 무엇이 선택돼 있었는지 보여야 하기 때문이다.
 */
export interface SearchableDropdownProps<V extends string | number = string> {
  value: V | null | undefined;
  options: readonly (V | Option<V>)[];
  onChange: (value: V) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  /**
   * 표 액션 줄(`DataTable` 의 `actions`)처럼 26px 버튼과 나란히 서는 자리.
   * 기본 높이는 33px 라 버튼 옆에 두면 그 줄만 키를 키운다 (CLAUDE.md §버튼 높이).
   */
  compact?: boolean;
}

function normalize<V extends string | number>(
  options: readonly (V | Option<V>)[]
): Option<V>[] {
  return options.map((opt) =>
    typeof opt === "object" && opt !== null && "value" in opt
      ? (opt as Option<V>)
      : ({ label: String(opt), value: opt as V } as Option<V>)
  );
}

export default function SearchableDropdown<V extends string | number = string>({
  value,
  options,
  onChange,
  placeholder = "선택",
  label,
  className = "",
  disabled = false,
  allowEmpty = false,
  emptyLabel = "(없음)",
  compact = false,
}: SearchableDropdownProps<V>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // 목록은 body 로 포털해 표의 overflow 컨테이너에 잘리지 않게 한다.
  const { anchorRef, menuRef, style, reposition, isOutside } = useAnchoredMenu(open);

  const normalized = useMemo(() => normalize(options), [options]);
  const selected = normalized.find((opt) => opt.value === value);

  /**
   * 목록은 **자르지 않는다.** 예전에는 앞의 200건만 그렸는데, 장소가 819곳이라 스크롤을
   * 끝까지 내려도 `대성MDI(석교)` 에서 멈췄다. 검색하면 나오니 더 헷갈렸다 — 필터를 먼저
   * 걸고 자르기 때문이다. 잘렸다는 표시조차 없어 목록이 그게 전부인 줄 알게 된다.
   *
   * 1,000개 남짓한 버튼은 열 때만 그리므로 그릴 만하다. 이보다 훨씬 늘어나면 그때
   * 가상 스크롤을 넣되, **조용히 자르는 것으로 되돌리지는 말 것.**
   */
  const filtered = useMemo(() => {
    const terms = searchTerms(search);
    if (terms.length === 0) return normalized;
    return normalized.filter((opt) => matchesTerms(terms, opt.label, opt.hint));
  }, [normalized, search]);

  useEffect(() => {
    if (open) {
      reposition();
      inputRef.current?.focus();
      setHighlight(0);
    }
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (isOutside(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, isOutside]);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  const commit = (next: V) => {
    onChange(next);
    close();
    // 목록은 onMouseDown 에서 preventDefault 하므로 커서는 그대로 입력칸에 남는다.
    // 여기서 focus() 를 다시 부르면 onFocus 가 목록을 도로 연다.
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      close();
      return;
    }
    if (!open) {
      // 닫혀 있을 때 방향키·글자키를 누르면 바로 열어 검색을 시작한다.
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      // 폼 안에서 쓰이므로 Enter 가 제출로 새어 나가지 않게 항상 막는다.
      e.preventDefault();
      if (filtered[highlight]) commit(filtered[highlight].value);
    } else if (e.key === "Tab") {
      close();
    }
  };

  return (
    <div ref={containerRef} className={className}>
      {label && <div className="mb-1 text-xs text-gray-500">{label}</div>}
      <input
        ref={(el) => {
          inputRef.current = el;
          anchorRef.current = el;
        }}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        // 닫혀 있으면 고른 값을, 열려 있으면 검색어를 담는다.
        value={open ? search : (selected?.label ?? "")}
        placeholder={open ? (selected?.label ?? placeholder) : placeholder}
        onChange={(e) => {
          if (!open) setOpen(true);
          setSearch(e.target.value);
          setHighlight(0);
        }}
        // 클릭·타이핑·방향키로만 연다. Tab 으로 지나갈 때까지 목록이 열리면
        // 폼을 훑는 동안 메뉴가 계속 튀어나온다.
        onMouseDown={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className={`w-full border-b bg-transparent px-0 text-left transition-colors placeholder:text-gray-300 focus:outline-none ${
          compact ? "py-1 text-xs" : "py-1.5 text-sm"
        } ${
          disabled
            ? "cursor-not-allowed border-gray-100 text-gray-300"
            : open
              ? "border-primary-600 text-gray-900"
              : "cursor-pointer border-gray-200 text-gray-900 hover:border-gray-400"
        }`}
      />

      {open && !disabled && style &&
        createPortal(
          <div
            ref={menuRef}
            style={style}
            className="flex flex-col border border-gray-300 bg-white shadow-sm"
          >
          <div className="flex-1 overflow-y-auto">
            {allowEmpty && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit("" as V);
                }}
                className="block w-full px-3 py-1.5 text-left text-sm text-gray-400 hover:bg-gray-100"
              >
                {emptyLabel}
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                일치하는 항목이 없습니다
              </div>
            ) : (
              filtered.map((opt, idx) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  disabled={opt.disabled}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(opt.value);
                  }}
                  className={`flex w-full items-baseline justify-between px-3 py-1.5 text-left text-sm disabled:text-gray-300 ${
                    idx === highlight ? "bg-primary-50 text-primary-800" : ""
                  } ${opt.value === value ? "font-medium text-gray-900" : "text-gray-700"}`}
                >
                  <ColorText color={opt.color}>{opt.label}</ColorText>
                  {opt.hint && (
                    <span className="ml-2 shrink-0 whitespace-nowrap text-xs text-gray-400">{opt.hint}</span>
                  )}
                </button>
              ))
            )}
          </div>
          </div>,
          document.body
        )}
    </div>
  );
}
